/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { useQueryClient, useSuspenseQueries } from '@tanstack/react-query';
import type { EntityBase } from '@opengov/psp-contract';
import type { Entity, EntityApplication } from '../types';
import { extractPlatformUser, extractPrimaryEntity } from '../utils/auth0UserUtils';
import { extractBaseEntity } from '../utils/entityUtils';
import { useEntitledEntitiesQuery } from '../hooks/useEntitledEntitiesQuery';
import { activeEntityEntitlementsQueryOptions } from '../hooks/useActiveEntityEntitlementsQuery';
import { entityProductEntitlementsQueryOptions } from '../hooks/useEntityProductEntitlementsQuery';
import { activeEntityClaimsQueryOptions } from '../hooks/useActiveEntityClaimsQuery';
import { useAuth } from '@/hooks/useAuth';
import { useLoggerWithContext } from '@/services/logger/loggerContext';
import { shellEventBus } from '../services/shellBridge';

/** Claim domain whose claims gate shell menu items (`requiredClaimPrefixes`). */
const PLATFORM_CLAIM_DOMAIN = 'PLATFORM';

// Cookie helper functions
const ENTITY_COOKIE_NAME = 'psp_active_entity';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year in seconds

function setEntityCookie(friendlyId: string) {
  document.cookie = `${ENTITY_COOKIE_NAME}=${friendlyId}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
}

function getEntityCookie(): string | null {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === ENTITY_COOKIE_NAME) {
      return value;
    }
  }
  return null;
}

function selectDefaultEntity(
  entities: EntityBase[],
  defaultFriendlyId: string | undefined
): EntityBase | null {
  if (!entities.length) return null;

  // 1. Previously selected entity from cookie (if still entitled)
  const savedId = getEntityCookie();
  if (savedId) {
    const found = entities.find((e: Entity) => e.friendlyId === savedId);
    if (found) {
      console.log('[EntityContext] Restored entity from cookie:', savedId);
      return extractBaseEntity(found);
    }
    console.log('[EntityContext] Cookie entity not found in entitled entities:', savedId);
  }

  // 2. Default user entity from Auth0 user object
  if (defaultFriendlyId) {
    const found = entities.find((e: Entity) => e.friendlyId === defaultFriendlyId);
    if (found) return extractBaseEntity(found);
  }

  // 3. First entitled entity
  return extractBaseEntity(entities[0]);
}

interface EntityContextType {
  entitledEntities: EntityBase[];
  activeEntity: EntityBase | null;
  activeEntityEntitledApplications: EntityApplication[];
  entityProductEntitlements: EntityApplication[];
  /**
   * PLATFORM claim keys the user holds at the active entity. Empty when the
   * Policy API failed or timed out (fail closed). Gates `requiredClaimPrefixes`.
   */
  activeEntityClaimKeys: ReadonlySet<string>;
  setActiveEntityByFriendlyId: (friendlyId: string) => void;
}

const EntityContext = createContext<EntityContextType | undefined>(undefined);

interface EntityProviderProps {
  readonly children: ReactNode;
}

export function EntityProvider({ children }: EntityProviderProps) {
  const { user, getAccessTokenSilently } = useAuth();
  const queryClient = useQueryClient();
  const logger = useLoggerWithContext();

  const userUuid = useMemo(() => extractPlatformUser(user)?.uuid, [user]);
  const defaultUserEntityFriendlyId = useMemo(() => extractPrimaryEntity(user)?.subdomain, [user]);

  const logEntitySelection = useCallback(
    (entity: EntityBase) => {
      logger.info(
        `New entity selected: ${entity.name}`,
        {
          entity_id: entity.entityId,
          entity_name: entity.name,
          entity_friendlyId: entity.friendlyId,
        },
        { persistContext: true }
      );
    },
    [logger]
  );

  // Bail out before reaching any suspense query if auth context is missing.
  // `user` (and thus userUuid) comes from the shared Auth0 context, so it's
  // available as soon as ProtectedRoute passes. The token is fetched inside
  // each queryFn via getAccessTokenSilently (cached / instant after first auth).
  if (!userUuid) {
    throw new Error('EntityProvider requires an authenticated user');
  }

  // Fetch all entitled entities (suspends until data is available)
  const { data: entitledEntities } = useEntitledEntitiesQuery({
    userUuid,
    getAccessTokenSilently,
    logger,
  });

  // Compute initial active entity synchronously — no waterfall render.
  // useState initializer only runs on mount (the first render after suspense resolves).
  const [activeEntity, setActiveEntity] = useState<EntityBase | null>(() =>
    selectDefaultEntity(entitledEntities, defaultUserEntityFriendlyId)
  );

  // Log whenever active entity changes (initial selection + user-initiated switches)
  useEffect(() => {
    if (activeEntity) {
      logEntitySelection(activeEntity);
    }
  }, [activeEntity, logEntitySelection]);

  // Bail before second query if there are no entitled entities for this user
  if (!activeEntity) {
    throw new Error('No entitled entities available for this user');
  }

  // Fetch per-user entitlements, entity-level (user-independent) product
  // entitlements and the user's PLATFORM claims in parallel — all only depend
  // on activeEntity, not on each other, so sequential useSuspenseQuery calls
  // would add avoidable network round trips. The entity-level one gates UI
  // that should reflect the entity's product subscription rather than this
  // particular user's individual access grant. The claims gate claim-based
  // menu items (e.g. Audit Logs); that query is capped by a timeout and fails
  // closed, so it never holds or breaks the shell.
  const [
    { data: activeEntityEntitledApplications },
    { data: entityProductEntitlements },
    { data: activeEntityClaims },
  ] = useSuspenseQueries({
    queries: [
      activeEntityEntitlementsQueryOptions({
        userUuid,
        friendlyId: activeEntity.friendlyId,
        getAccessTokenSilently,
        logger,
      }),
      entityProductEntitlementsQueryOptions({
        entityUuid: activeEntity.id,
        getAccessTokenSilently,
        logger,
        queryClient,
      }),
      activeEntityClaimsQueryOptions({
        userUuid,
        entityUuid: activeEntity.id,
        domain: PLATFORM_CLAIM_DOMAIN,
        getAccessTokenSilently,
        logger,
        queryClient,
      }),
    ],
  });

  const activeEntityClaimKeys = useMemo(() => new Set(activeEntityClaims), [activeEntityClaims]);

  // Set active entity by friendlyId (validates against entitled entities)
  const setActiveEntityByFriendlyId = useCallback(
    (friendlyId: string) => {
      const entity = entitledEntities.find((e) => e.friendlyId === friendlyId);
      if (!entity) {
        console.warn(`[EntityContext] Entity "${friendlyId}" not found in entitled entities`);
        return;
      }

      const newEntity = extractBaseEntity(entity);
      setActiveEntity(newEntity);
      setEntityCookie(friendlyId);
      shellEventBus.emit('entityChange', newEntity);
    },
    [entitledEntities]
  );

  const value: EntityContextType = useMemo(
    () => ({
      entitledEntities,
      activeEntity,
      activeEntityEntitledApplications,
      entityProductEntitlements,
      activeEntityClaimKeys,
      setActiveEntityByFriendlyId,
    }),
    [
      entitledEntities,
      activeEntity,
      activeEntityEntitledApplications,
      entityProductEntitlements,
      activeEntityClaimKeys,
      setActiveEntityByFriendlyId,
    ]
  );

  return <EntityContext.Provider value={value}>{children}</EntityContext.Provider>;
}

export function useEntity() {
  const context = useContext(EntityContext);
  if (context === undefined) {
    throw new Error('useEntity must be used within an EntityProvider');
  }
  return context;
}
